import { NAV_NUMBER } from "../../../config/config";
import {
  createColumn,
  formatAggregatedFooter,
  formatNumericCell,
  formatPercentageCell,
  formatPriceCell,
  getCommonBodyCellProps,
  getCommonFooterCellProps,
  getCommonHeadCellProps,
} from "./commonUtils";
import React from "react";
import { formatPosition } from "../../../utils/dataCalculations";

export const getRiskTableColumns = (
  theme,
  globalFilter,
  formatCurrency,
  formatNumber
) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const textStr = String(text);
    const searchStr = String(searchTerm);
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);
    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            padding: "1px 2px",
            borderRadius: theme.shape.borderRadius * 0.5,
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      140,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) => highlightMatch(cell.getValue(), globalFilter),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "risk_to_downside_pct_nav",
      "Risk as % of AUM",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const a = Number(rowA.original.risk_to_downside) / NAV_NUMBER || 0;
            const b = Number(rowB.original.risk_to_downside) / NAV_NUMBER || 0;
            return a - b;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              total += Number(row.original.risk_to_downside) || 0;
            }
            return total / NAV_NUMBER;
          },
          Cell: ({ row }) => {
            const value = row.original.risk_to_downside;
            if (!value) return "-";
            const bpsValue = (Number(value) / NAV_NUMBER) * 10000;
            return formatPercentageCell(
              bpsValue,
              1,
              " bps",
              theme.palette.error.main,
              theme.palette.text.primary
            );
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue();
            if (value === null || value === undefined) return "-";
            const bpsValue = value * 10000;
            return formatPercentageCell(
              bpsValue,
              1,
              " bps",
              theme.palette.error.main,
              theme.palette.text.primary
            );
          },
          // Footer: ({ table }) => {
          //   const total =
          //     table
          //       .getFilteredRowModel()
          //       .rows.reduce(
          //         (sum, row) =>
          //           sum + (Number(row.original.risk_to_downside) || 0),
          //         0,
          //       ) / NAV_NUMBER;
          //   if (!total || total === 0) return "-";
          //   const bpsValue = total * 10000;
          //   const roundedValue = Number(bpsValue).toFixed(1);
          //   const formattedValue =
          //     Number(roundedValue) > 0
          //       ? `+${roundedValue} bps`
          //       : `${roundedValue} bps`;
          //   return (
          //     <span
          //       style={{
          //         color:
          //           Number(roundedValue) < 0
          //             ? theme.palette.error.main
          //             : theme.palette.text.primary,
          //         fontWeight: Number(roundedValue) < 0 ? 600 : 400,
          //         fontSize: "14px",
          //       }}
          //     >
          //       {formattedValue}
          //     </span>
          //   );
          // },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "lmv",
      "LMV",
      120,
      "right",
      {
        other: {
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue > 0 ? marketValue : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue > 0 ? marketValue : 0;
            return formatNumericCell(
              value,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            );
          },
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue > 0 ? marketValue : 0);
              }, 0);

            if (!total || total === 0) return "-";
            const roundedTotal = Math.round(total);
            const formattedTotal = formatCurrency(roundedTotal);
            return (
              <span
                style={{
                  color:
                    roundedTotal < 0
                      ? theme.palette.error.main
                      : theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      200,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) => highlightMatch(cell.getValue(), globalFilter),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Current Qty",
      90,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "last_price_in_monitor",
      "Last Price in Monitor",
      90,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Dealbreak_Downside_Price",
      "Deal Break Price",
      90,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "gross_pnl_to_dealclose",
      "Gross PnL to Deal Close",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const a = Number(rowA.getValue("gross_pnl_to_dealclose")) || 0;
            const b = Number(rowB.getValue("gross_pnl_to_dealclose")) || 0;
            return a - b;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              total += Number(row.original.gross_pnl_to_dealclose) || 0;
            }
            return total;
          },
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                return sum + (Number(row.original.gross_pnl_to_dealclose) || 0);
              }, 0);

            if (!total || total === 0) return "-";
            const roundedTotal = Math.round(total);
            const formattedTotal = formatCurrency(roundedTotal);
            return (
              <span
                style={{
                  color:
                    roundedTotal < 0
                      ? theme.palette.error.main
                      : theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_pnl_to_dealclose",
      "Net PnL to Deal Close",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const a = Number(rowA.getValue("net_pnl_to_dealclose")) || 0;
            const b = Number(rowB.getValue("net_pnl_to_dealclose")) || 0;
            return a - b;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              total += Number(row.original.net_pnl_to_dealclose) || 0;
            }
            return total;
          },
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                return sum + (Number(row.original.net_pnl_to_dealclose) || 0);
              }, 0);

            if (!total || total === 0) return "-";
            const roundedTotal = Math.round(total);
            const formattedTotal = formatCurrency(roundedTotal);
            return (
              <span
                style={{
                  color:
                    roundedTotal < 0
                      ? theme.palette.error.main
                      : theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "risk_to_downside",
      "Risk to Downside",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const a = Number(rowA.getValue("risk_to_downside")) || 0;
            const b = Number(rowB.getValue("risk_to_downside")) || 0;
            return a - b;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const value = row.original.risk_to_downside;
              if (value !== null && value !== undefined) {
                total += Number(value);
              }
            }
            return total;
          },
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                return sum + (Number(row.original.risk_to_downside) || 0);
              }, 0);

            if (!total || total === 0) return "-";
            const roundedTotal = Math.round(total);
            const formattedTotal = formatCurrency(roundedTotal);
            return (
              <span
                style={{
                  color:
                    roundedTotal < 0
                      ? theme.palette.error.main
                      : theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "gross_spread",
      "Gross Spread",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 2),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "gross_spread_percentage",
      "Gross Spread %",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatPercentageCell(cell.getValue() * 100, 2, "%"),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "gross_ror",
      "Gross RoR",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatPercentageCell(cell.getValue() * 100, 2, "%"),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "implied_probability",
      "Implied Prob",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            if (!isNaN(value) && value !== null && value !== "") {
              return formatPercentageCell(value * 100, 1, "%");
            }
            return value || "-";
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_spread",
      "Net Spread",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 2),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_ror",
      "Net RoR",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatPercentageCell(cell.getValue() * 100, 1, "%"),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_implied_probability",
      "Net Implied Prob",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            if (!isNaN(value) && value !== null && value !== "") {
              return formatPercentageCell(value * 100, 1, "%");
            }
            return value || "-";
          },
        },
      }
    ),

    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "closing_date",
      "Closing Date",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "market_value",
      "Market Value",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(cell.getValue(), formatCurrency),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(cell.getValue(), formatCurrency),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                return sum + (Number(row.original.market_value) || 0);
              }, 0);

            if (!total || total === 0) return "-";
            const roundedTotal = Math.round(total);
            const formattedTotal = formatCurrency(roundedTotal);
            return (
              <span
                style={{
                  color:
                    roundedTotal < 0
                      ? theme.palette.error.main
                      : theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "smv",
      "SMV",
      120,
      "right",
      {
        other: {
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue < 0 ? Math.abs(marketValue) : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue < 0 ? Math.abs(marketValue) : 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue() || 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue < 0 ? Math.abs(marketValue) : 0);
              }, 0);

            if (!total || total === 0) return "-";
            const roundedTotal = Math.round(total);
            const formattedTotal = `(${formatCurrency(roundedTotal).replace(
              "$",
              ""
            )})`;
            return (
              <span
                style={{
                  color:
                    roundedTotal > 0 ? theme.palette.error.main : "inherit",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "market_value_pct_nav",
      "MV as % of AUM",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const a = Number(rowA.original.market_value) / NAV_NUMBER || 0;
            const b = Number(rowB.original.market_value) / NAV_NUMBER || 0;
            return a - b;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const value = row.original.market_value;
              if (value !== null && value !== undefined) {
                total += Number(value);
              }
            }
            return total / NAV_NUMBER;
          },
          Cell: ({ row }) => {
            const value = row.original.market_value;
            if (!value) return "-";
            const percentValue = (Number(value) / NAV_NUMBER) * 100;
            return formatPercentageCell(percentValue, 2, "%");
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue();
            if (value === null || value === undefined) return "-";
            const percentValue = value * 100;
            return formatPercentageCell(percentValue, 2, "%");
          },
          Footer: ({ table }) => {
            const total =
              table
                .getFilteredRowModel()
                .rows.reduce(
                  (sum, row) => sum + (Number(row.original.market_value) || 0),
                  0
                ) / NAV_NUMBER;
            if (!total || total === 0) return "-";
            const percentValue = total * 100;
            const roundedValue = Number(percentValue).toFixed(2);
            const formattedValue =
              Number(roundedValue) > 0
                ? `+${roundedValue}%`
                : `${roundedValue}%`;
            return (
              <span
                style={{
                  color:
                    Number(roundedValue) < 0
                      ? theme.palette.error.main
                      : theme.palette.text.primary,
                  fontWeight: Number(roundedValue) < 0 ? 600 : 400,
                  fontSize: "14px",
                }}
              >
                {formattedValue}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "lmv_pct_nav",
      "LMV as % of AUM",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const aValue =
              Number(rowA.original.market_value) > 0
                ? Number(rowA.original.market_value)
                : 0;
            const bValue =
              Number(rowB.original.market_value) > 0
                ? Number(rowB.original.market_value)
                : 0;
            return aValue / NAV_NUMBER - bValue / NAV_NUMBER;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue > 0 ? marketValue : 0;
            }
            return total / NAV_NUMBER;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue > 0 ? marketValue : 0;
            if (value === 0) return "-";
            const percentValue = (value / NAV_NUMBER) * 100;
            return formatPercentageCell(percentValue, 2, "%");
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue();
            if (value === null || value === undefined || value === 0)
              return "-";
            const percentValue = value * 100;
            return formatPercentageCell(percentValue, 2, "%");
          },
          Footer: ({ table }) => {
            const total =
              table.getFilteredRowModel().rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue > 0 ? marketValue : 0);
              }, 0) / NAV_NUMBER;
            if (total === 0) return "-";
            const percentValue = total * 100;
            const roundedValue = Number(percentValue).toFixed(2);
            const formattedValue = `+${roundedValue}%`;
            return (
              <span
                style={{
                  color: theme.palette.text.primary,
                  fontWeight: 400,
                  fontSize: "14px",
                }}
              >
                {formattedValue}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "smv_pct_nav",
      "SMV as % of AUM",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const aValue =
              Number(rowA.original.market_value) < 0
                ? Math.abs(Number(rowA.original.market_value))
                : 0;
            const bValue =
              Number(rowB.original.market_value) < 0
                ? Math.abs(Number(rowB.original.market_value))
                : 0;
            return aValue / NAV_NUMBER - bValue / NAV_NUMBER;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue < 0 ? Math.abs(marketValue) : 0;
            }
            return total / NAV_NUMBER;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue < 0 ? Math.abs(marketValue) : 0;
            if (value === 0) return "-";
            const percentValue = (value / NAV_NUMBER) * 100;
            const roundedValue = Number(percentValue).toFixed(2);
            const formattedValue = `-${roundedValue}%`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue();
            if (value === null || value === undefined || value === 0)
              return "-";
            const percentValue = value * 100;
            const roundedValue = Number(percentValue).toFixed(2);
            const formattedValue = `-${roundedValue}%`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const total =
              table.getFilteredRowModel().rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue < 0 ? Math.abs(marketValue) : 0);
              }, 0) / NAV_NUMBER;
            if (total === 0) return "-";
            const percentValue = total * 100;
            const roundedValue = Number(percentValue).toFixed(2);
            const formattedValue = `-${roundedValue}%`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "searchStrategyName",
      "Search Strategy",
      0,
      "left",
      {
        head: { display: "none" },
        body: { display: "none" },
        footer: { display: "none" },
        other: {
          id: "searchStrategyName",
          accessorKey: "StrategyName",
          enableHiding: true,
          enableSorting: false,
          enableColumnFilter: false,
          enableGlobalFilter: true,
          Cell: ({ cell }) => null,
        },
      }
    ),
  ];
};
